import { useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import Seo from '../../components/Seo'
import { api } from '../../api/client'
import { brand } from '../../brand'

/**
 * Suppression by token. The link in every outreach email carries an opaque
 * token that identifies the recipient address server-side. The page never
 * shows the company name or any internal data: it confirms the request and
 * nothing else.
 */
export default function OptOut() {
  const [params] = useSearchParams()
  const token = params.get('token') || params.get('t')
  const pending = useRef<{ token: string; promise: Promise<{ ok: true }> } | null>(null)
  const [state, setState] = useState<'idle' | 'working' | 'done' | 'error'>('idle')

  async function confirmOptOut() {
    if (!token || pending.current) return
    setState('working')
    const promise = api.optOut(token)
    pending.current = { token, promise }
    try { await promise; setState('done') }
    catch { setState('error') }
  }


  return (
    <>
      <Seo title="Opt out" noindex />
      <section className="hero">
        <span className="eyebrow">Opt out</span>
        <h1 style={{ marginTop: '0.75rem' }}>Stop contact from {brand.name}</h1>
      </section>
      <section className="section">
        {token ? (
          state === 'done' ? (
            <div className="notice" role="status">
              <strong>Done.</strong> We have recorded your request and will not contact your company again.
            </div>
          ) : state === 'error' ? (
            <div className="notice crit" role="alert">
              This link is not valid or has already been used. To opt out, reply to any email from us with the word
              “unsubscribe”
              , or write to <a href={`mailto:${brand.privacyEmail}`}>{brand.privacyEmail}</a>.
            </div>
          ) : (
            <div>
              <p>Confirm that you want to stop procurement outreach to your company.</p>
              <button className="btn" disabled={state === 'working'} onClick={() => { void confirmOptOut() }} style={{ marginTop: '1rem' }}>{state === 'working' ? 'Recording your request…' : 'Confirm opt-out'}</button>
            </div>
          )
        ) : (
          <>
            <p className="lede">
              Use the opt-out link in any email we sent you. It works without logging in and applies to your
              company after you confirm.
            </p>
            <p style={{ marginTop: '1rem' }}>
              If you no longer have the email, reply to any message from us with the word “unsubscribe”, say “take
              me off your list” during a call, or write to{' '}
              <a href={`mailto:${brand.privacyEmail}`}>{brand.privacyEmail}</a>.
            </p>
          </>
        )}
      </section>
    </>
  )
}
